<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  yacht: { type: Object, required: true },
  equipment: { type: Object, default: null },
  fallbackImage: { type: String, required: true }
})

defineEmits(['open-gallery'])

const { t } = useI18n()

const showFullDescription = ref(false)
const equipmentShow = ref(false)

const descriptionMaxLength = 450

const isDescriptionLong = computed(() => {
  return (props.yacht?.description?.length || 0) > descriptionMaxLength
})

const truncatedDescription = computed(() => {
  const desc = props.yacht?.description ?? ''
  if (showFullDescription.value) return desc
  return desc.slice(0, descriptionMaxLength) + (desc.length > descriptionMaxLength ? '...' : '')
})

const toggleDescription = () => {
  showFullDescription.value = !showFullDescription.value
}

const mainImage = computed(() => {
  return (
      props.yacht?.mainImage?.url ||
      props.yacht?.mainImage?.name ||
      null
  )
})

function handleImageError(e: Event) {
  const img = e.target as HTMLImageElement
  img.src = props.fallbackImage
  img.onerror = null
}
</script>




<template>
  <div class="flex container boat-overview-content my-12 gap-x-12">
    <!-- LEFT: IMAGE -->
    <div class="boat-overview-content-left w-2/3">
      <img
          v-if="mainImage"
          :src="mainImage"
          alt="boat"
          class="main-image rounded-8xl w-full h-[550px] object-cover cursor-pointer"
          @click="$emit('open-gallery')"
          @error="handleImageError"
      />

      <img
          v-else
          :src="fallbackImage"
          alt="boat"
          class="main-image rounded-8xl w-[650px] h-[350px] object-fit cursor-pointer"
          @click="$emit('open-gallery')"
          @error="handleImageError"
      />
    </div>

    <!-- RIGHT: OVERVIEW -->
    <div class="boat-overview-content-right w-1/3 flex flex-col pl-0 sm:pl-6 py-6 gap-8">
      <!-- DESCRIPTION -->
      <div class="flex flex-col gap-4">
        <p class="responsive-title-4 font-semibold">
          {{ t('overview') }}
        </p>

        <div class="flex flex-col gap-3">
          <p class="responsive-body-large">
            {{ truncatedDescription }}
          </p>

          <p
              v-if="isDescriptionLong"
              @click="toggleDescription"
              class="responsive-body-medium font-semibold underline cursor-pointer transition-opacity hover:opacity-60"
          >
            {{ showFullDescription ? t('readLess') : t('readMore') }}
          </p>
        </div>
      </div>

      <!-- EQUIPMENT -->
      <div v-if="equipment?.total > 0">
        <div class="flex flex-col gap-5">
          <span class="responsive-title-6 font-semibold">
            {{ t('equipment') }}
          </span>

          <div class="flex gap-1 flex-wrap">
            <div v-for="item in equipment.show" :key="item.id">
              <div class="bg-bw-lightGrey py-1 px-2 rounded-15xl">
                <span class="text-sm">
                  {{ item.name }}
                </span>
              </div>
            </div>

            <div
                v-for="item in equipment.hide"
                :key="item.id"
                v-show="equipmentShow"
            >
              <div class="bg-bw-lightGrey py-1 px-2 rounded-15xl">
                <span class="text-sm">
                  {{ item.name }}
                </span>
              </div>
            </div>
          </div>

          <span
              v-if="!equipmentShow && equipment.hidden > 0"
              @click="equipmentShow = true"
              class="responsive-body-medium font-semibold underline cursor-pointer transition-opacity hover:opacity-60"
          >
            {{ t('more', { count: equipment.hidden }) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 1160px) {
  .boat-overview-content {
    flex-direction: column;
  }

  .boat-overview-content .boat-overview-content-left {
    width: 100%;
  }

  .boat-overview-content .boat-overview-content-right {
    width: 100%;
  }

  .boat-overview-content-left .main-image {
    height: 400px;
  }

  .boat-overview-content-right {
    padding-right: 0;
    padding-left: 0;
  }

  .main-image {
    border-width: thin !important;
    border-style: solid !important;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  }
}
</style>